import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const PRINCIPLES = [
  { num: "01", title: "Depth over volume", desc: "A small, deliberate portfolio so each brand gets genuine attention." },
  { num: "02", title: "Senior craftsmanship", desc: "Every engagement led by senior practitioners with measurable outcomes." },
  { num: "03", title: "Clarity before campaigns", desc: "Strategy, positioning and narrative sharpened before deployment." },
  { num: "04", title: "Measurable impact", desc: "Pipeline, payback and brand equity measured in business outcomes." },
];

export default function About() {
  useEffect(() => { document.title = "About — The Indian Marketing"; }, []);

  return (
    <div data-testid="about-page" className="bg-[#2E2E31] text-white">
      <section className="pt-40 pb-20 md:pt-48 md:pb-28 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="font-outfit text-xs uppercase tracking-[0.3em] text-[#D4AF37]">About The Studio</span>
          </div>
          <h1 className="font-playfair text-5xl md:text-6xl lg:text-7xl text-white tracking-tight leading-[1.05] max-w-5xl">
            We Are a Boutique Studio for <em className="text-[#D4AF37] font-normal">Brand Craft.</em>
          </h1>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="lg:col-span-7 space-y-8 font-outfit text-lg text-[#D9D9D9] leading-relaxed"
          >
            <p>
              We are a boutique digital marketing and creative strategy firm dedicated to crafting refined, high-impact brand experiences.
            </p>
            <p>
              Our approach is intentionally selective. We work with a limited number of clients to ensure every brand receives bespoke strategy, senior-level insight and meticulous execution.
            </p>
            <p>
              Every project is treated as a handcrafted partnership.
            </p>
            <p>
              We blend aesthetics with analytics, creativity with precision, and storytelling with performance to create digital experiences that feel elegant, intentional and unforgettable.
            </p>
            <p>
              From premium content and high-performing campaigns to sophisticated websites and visual identities, our work is shaped by craftsmanship, attention to detail and a deep understanding of your brand's essence.
            </p>
            <p className="text-white font-playfair text-2xl md:text-3xl leading-snug pt-4 border-l-2 border-[#D4AF37] pl-6">
              We are not a volume-driven agency. We are a boutique partner for brands that value excellence, individuality and a seamless creative journey.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative"
          >
            <div className="aspect-[4/5] overflow-hidden zoom-on-hover">
              <img
                src="https://images.unsplash.com/photo-1698500278205-bf650c97a1f3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGdvbGQlMjB0ZXh0dXJlJTIwZGFya3xlbnwwfHx8fDE3ODA4NTYxODZ8MA&ixlib=rb-4.1.0&q=85"
                alt="Editorial brand craft"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#D4AF37] text-black p-6 max-w-xs">
              <div className="font-playfair text-3xl leading-none">An Ikoa Edge Company</div>
              <div className="font-outfit text-[11px] uppercase tracking-[0.25em] mt-3">Parent brand · Established craft</div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#111111]" data-testid="principles-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="font-outfit text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Operating Principles</span>
          </div>
          <h2 className="font-playfair text-4xl md:text-5xl text-white tracking-tight leading-tight max-w-3xl">
            How we work, what we stand for.
          </h2>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12">
            {PRINCIPLES.map((p, i) => (
              <motion.div
                key={i}
                data-testid={`principle-${i}`}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}
                className="border-t border-white/10 pt-8"
              >
                <div className="font-playfair text-[#D4AF37] text-2xl mb-4">{p.num}</div>
                <h3 className="font-playfair text-2xl md:text-3xl text-white tracking-tight">{p.title}</h3>
                <p className="font-outfit text-base text-[#D9D9D9] leading-relaxed mt-4">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-playfair text-4xl md:text-5xl text-white tracking-tight leading-tight">
            Curious to see if we're a fit?
          </h2>
          <p className="font-outfit text-lg text-[#D9D9D9] mt-6 max-w-2xl mx-auto">
            We open a limited number of engagements each quarter. A 30-minute conversation tells us both whether the chemistry is right.
          </p>
          <Link
            to="/contact"
            data-testid="about-cta-contact"
            className="inline-flex items-center gap-3 mt-10 bg-[#D4AF37] text-black font-outfit font-medium px-10 py-5 uppercase tracking-[0.2em] text-sm hover:bg-[#F0C84B] transition-colors"
          >
            Start the Conversation <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
